<script lang="ts">
    import { BriefStore } from '../../stores/BriefStore';
    import { api } from '../../framework/js/api.js';
    import { onMount } from 'svelte';
    import {
        AppEmptyState,
        AppLoadingState,
        AppErrorState,
        AppToolbar,
        SortControl,
        StatusPills,
        AssessmentTile,
        AssessmentPager,
        relativeTime,
        matchesSearch,
        paginate,
    } from '../../framework/components/app';
    import type { RingSpec, RingTone, StatusTone, PillOption } from '../../framework/components/app';

    interface BriefRow {
        uuid?: string;
        id?: string | number;
        name?: string | null;
        xplorit?: { name?: string } | null;
        project_name?: string | null;
        /** xplorit (project) row — list API exposes this as created_at */
        created_at: string;
        /** xplorit_briefs.created_at from list API */
        brief_created_at?: string;
        /** xplorit_briefs.updated_at — last save / touch */
        updated_at?: string;
        last_generated?: string | null;
        version_count?: number;
        alignment_percent?: number | null;
        alignment_status?: string | null;
    }

    let briefs: BriefRow[] = [];
    let state: 'loading' | 'ready' | 'error' = 'loading';
    let searchQuery = '';
    let statusFilter: string[] = [];
    let sortField: 'newest' | 'oldest' | 'titleAsc' | 'titleDesc' = 'newest';
    let page = 1;
    let pageSize = 20;

    const STATUS_OPTIONS: PillOption[] = [
        { value: 'generated', label: 'Generated' },
        { value: 'draft', label: 'Draft' },
    ];

    const SORT_OPTIONS = [
        { value: 'newest', label: 'Newest first' },
        { value: 'oldest', label: 'Oldest first' },
        { value: 'titleAsc', label: 'A – Z' },
        { value: 'titleDesc', label: 'Z – A' },
    ];

    function briefTitle(b: BriefRow): string {
        return b.name || b.xplorit?.name || b.project_name || 'Untitled Brief';
    }

    function briefKey(b: BriefRow): string {
        return String(b.uuid || b.id);
    }

    /** When the brief record was created (prefer brief row over parent xplorit). */
    function briefCreatedAt(b: BriefRow): string {
        return b.brief_created_at || b.created_at;
    }

    /** Last activity for sorting drafts (edit time, then creation). */
    function briefActivityAt(b: BriefRow): string {
        return b.last_generated || b.updated_at || briefCreatedAt(b);
    }

    function briefStatus(b: BriefRow): 'generated' | 'draft' {
        return (b.version_count ?? 0) > 0 ? 'generated' : 'draft';
    }

    function alignmentTone(percent: number | null | undefined): RingTone {
        if (percent == null) return 'neutral';
        if (percent >= 75) return 'success';
        if (percent >= 60) return 'warn';
        return 'danger';
    }

    function alignmentBadgeTone(percent: number | null | undefined): StatusTone {
        if (percent == null) return 'neutral';
        if (percent >= 75) return 'success';
        if (percent >= 60) return 'warn';
        return 'danger';
    }

    async function loadData() {
        state = 'loading';
        try {
            const response: any = await api.getXploritBriefs('', 'generated', 'desc');
            if (response?.success && response?.data?.list) {
                briefs = response.data.list;
                BriefStore.set(briefs);
                state = 'ready';
            } else {
                state = 'error';
            }
        } catch {
            state = 'error';
        }
    }

    function onSearch(e: CustomEvent<string>) {
        searchQuery = e.detail;
        page = 1;
    }

    function onStatusChange(e: CustomEvent<string[]>) {
        statusFilter = e.detail;
        page = 1;
    }

    function onSort(e: CustomEvent<{ field: string }>) {
        sortField = e.detail.field as typeof sortField;
        page = 1;
    }

    function tileFor(b: BriefRow): {
        ring: RingSpec;
        ringTone: RingTone;
        statusLabel: string;
        statusTone: StatusTone;
        statusDetail: string;
        subtitle: string;
        footerNote: string;
        versionChip: string;
    } {
        const vc = b.version_count ?? 0;
        const isGenerated = vc > 0;
        const versionLabel = vc === 1 ? '1 version' : `${vc} versions`;

        if (isGenerated) {
            const percent = b.alignment_percent;
            return {
                ring: percent != null
                    ? { kind: 'percent', value: percent }
                    : { kind: 'icon', name: 'check' },
                ringTone: alignmentTone(percent),
                statusLabel: 'Generated',
                statusTone: alignmentBadgeTone(percent),
                statusDetail: versionLabel,
                subtitle: `Created ${relativeTime(briefCreatedAt(b))}`,
                footerNote: b.last_generated
                    ? `Generated ${relativeTime(b.last_generated)}`
                    : '',
                versionChip: `v${vc}`,
            };
        }

        return {
            ring: { kind: 'icon', name: 'pencil' },
            ringTone: 'neutral',
            statusLabel: 'Draft',
            statusTone: 'neutral',
            statusDetail: 'Not yet generated',
            subtitle: `Created ${relativeTime(briefCreatedAt(b))}`,
            footerNote: `Edited ${relativeTime(b.updated_at || briefCreatedAt(b))}`,
            versionChip: '',
        };
    }

    function compareItems(a: BriefRow, b: BriefRow, field: typeof sortField): number {
        if (field === 'newest') {
            const at = new Date(briefActivityAt(a)).getTime();
            const bt = new Date(briefActivityAt(b)).getTime();
            return bt - at;
        }
        if (field === 'oldest') {
            const at = new Date(briefActivityAt(a)).getTime();
            const bt = new Date(briefActivityAt(b)).getTime();
            return at - bt;
        }
        if (field === 'titleDesc') {
            return briefTitle(b).localeCompare(briefTitle(a));
        }
        return briefTitle(a).localeCompare(briefTitle(b));
    }

    $: filtered = briefs.filter((b) => {
        if (statusFilter.length > 0 && !statusFilter.includes(briefStatus(b))) return false;
        if (searchQuery.length >= 3) return matchesSearch(briefTitle(b), searchQuery);
        return true;
    });

    $: sorted = [...filtered].sort((a, b) => compareItems(a, b, sortField));
    $: totalCount = sorted.length;
    $: pagedItems = paginate(sorted, page, pageSize);

    $: statusCounts = STATUS_OPTIONS.map((o) => ({
        ...o,
        count: briefs.filter((b) => briefStatus(b) === o.value).length,
    }));

    onMount(loadData);
</script>

{#if state === 'loading'}
    <AppLoadingState label="Loading briefs" />
{:else if state === 'error'}
    <AppErrorState on:retry={loadData} />
{:else if briefs.length === 0}
    <AppEmptyState
        icon="file text"
        title="No briefs yet"
        description="Create your first research brief to see it here."
        actionLabel="Create brief"
        actionHref="/brief-new"
    />
{:else}
    <div class="filter-bar">
        <AppToolbar
            bind:searchValue={searchQuery}
            searchPlaceholder="Search by project name…"
            searchLabel="Search briefs"
            resultsCount={totalCount}
            resultsSingular="brief"
            resultsPlural="briefs"
            on:search={onSearch}
        >
            <SortControl
                slot="controls"
                options={SORT_OPTIONS}
                field={sortField}
                on:change={onSort}
            />
        </AppToolbar>
        <StatusPills
            options={statusCounts}
            selected={statusFilter}
            on:change={onStatusChange}
        />
    </div>

    {#if pagedItems.length === 0}
        <div class="empty-filter">
            <p>No briefs match your filters.</p>
            <button type="button" class="reset-btn" on:click={() => { searchQuery = ''; statusFilter = []; page = 1; }}>
                Clear filters
            </button>
        </div>
    {:else}
        <div class="tile-grid">
            {#each pagedItems as brief (briefKey(brief))}
                {@const t = tileFor(brief)}
                <AssessmentTile
                    agent="brief"
                    href={`/briefs/${brief.uuid || brief.id}`}
                    title={briefTitle(brief)}
                    subtitle={t.subtitle}
                    ring={t.ring}
                    ringTone={t.ringTone}
                    statusLabel={t.statusLabel}
                    statusTone={t.statusTone}
                    statusDetail={t.statusDetail}
                    versionChip={t.versionChip}
                    footerNote={t.footerNote}
                />
            {/each}
        </div>
    {/if}

    <AssessmentPager
        bind:page
        bind:pageSize
        totalCount={totalCount}
    />
{/if}

<style>
    .filter-bar {
        display: flex;
        flex-direction: column;
        gap: var(--space-3);
        margin-bottom: var(--space-5);
    }

    .tile-grid {
        display: grid;
        grid-template-columns: repeat(4, minmax(0, 1fr));
        gap: var(--space-6);
    }

    @media (max-width: 1440px) {
        .tile-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); }
    }

    @media (max-width: 1024px) {
        .tile-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
    }

    @media (max-width: 640px) {
        .tile-grid { grid-template-columns: 1fr; }
    }

    .empty-filter {
        text-align: center;
        padding: var(--space-8) var(--space-4);
        color: var(--grey-secondary);
    }

    .empty-filter p { margin: 0 0 var(--space-3) 0; }

    .reset-btn {
        background: transparent;
        border: 1px solid var(--border-color);
        border-radius: var(--radius-input);
        color: var(--primary);
        font: inherit;
        font-size: var(--text-sm);
        padding: var(--space-2) var(--space-4);
        cursor: pointer;
    }

    .reset-btn:hover { background: var(--surface-hover); border-color: var(--primary); }
</style>
